import React, { useState, useRef } from 'react';
import { FaRegPaperPlane } from 'react-icons/fa';
import emailjs from '@emailjs/browser';
import "../../assets/style/Contact.css"
import { useTranslation } from "react-i18next"
import ReactGA from 'react-ga4';

const EMAILJS_SERVICE_ID = "service_4woem4p";
const EMAILJS_TEMPLATE_ID = "template_qh2gdok";
const EMAILJS_PUBLIC_KEY = "PAG4_KDFLJt6C1KVq";

interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
}

const Formulaire: React.FC = () => {
  const { t } = useTranslation('fr', { useSuspense: false });
  const formRef = useRef<HTMLFormElement>(null);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('validation.nameRequired') || 'Le nom est requis';
    }
    if (!formData.email.trim()) {
      newErrors.email = t('validation.emailRequired') || "L'email est requis";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('validation.emailInvalid') || 'Format email invalide';
    }
    if (!formData.message.trim()) {
      newErrors.message = t('validation.messageRequired') || 'Le message est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('sending');
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Non renseigné',
          message: formData.message,
          reply_to: formData.email,
          sent_date: new Date().toLocaleString('fr-FR'),
        },
        EMAILJS_PUBLIC_KEY
      );
      setStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
      ReactGA.event({ category: 'Contact', action: 'form_submit' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="contact__content">
      <form ref={formRef} onSubmit={handleSubmit} className="contact__form" noValidate>

        {/* Nom + Email côte à côte */}
        <div className="form__row">
          <div className={`inputBox${formData.name ? ' has-value' : ''}`}>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
            <span>{t('name')}</span>
            {errors.name && <p className="form__error">{errors.name}</p>}
          </div>

          <div className={`inputBox${formData.email ? ' has-value' : ''}`}>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
            <span>E-mail</span>
            {errors.email && <p className="form__error">{errors.email}</p>}
          </div>
        </div>

        {/* Téléphone */}
        <div className={`inputBox${formData.phone ? ' has-value' : ''}`}>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
          />
          <span>{t('phone')}</span>
        </div>

        {/* Message */}
        <div className="input__container textarea">
          <label className="input__label">Message</label>
          <textarea
            name="message"
            className="input"
            placeholder="Votre message..."
            value={formData.message}
            onChange={handleChange}
          />
          {errors.message && <p className="form__error">{errors.message}</p>}
        </div>

        <div className="form__footer">
          <button type="submit" className="button" style={{ width: '180px', justifyContent: 'flex-end' }} disabled={status === 'sending'}>
            <FaRegPaperPlane className="button__icon" />
            {status === 'sending' ? '...' : t('sendMessage')}
          </button>

          <div className="form__status">
            {status === 'success' && (
              <p className="form__success">
                {t('messageSent') || 'Message envoyé avec succès !'}
              </p>
            )}
            {status === 'error' && (
              <p className="form__error-banner">
                {t('messageError') || "Erreur lors de l'envoi. Réessaie."}
              </p>
            )}
          </div>
        </div>

      </form>
    </div>
  );
};


export default Formulaire;
